import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '@material-table/core';
import DataTable from '../../components/shared/DataTable/DataTable';
import Badge from '../../components/shared/Badge/Badge';
import OptionsMenu from '../../components/shared/OptionsMenu/OptionsMenu';
import BtButton from '../bt-button/bt-button';
import { isOwnRecipientOption } from '../app-evaluation-start';
import { RequestStatus } from '../../services/service-evaluations';
import { EvaluationRecipientStatus, EvaluationRequestStatus } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import {
  recipientStatusMeta,
  requestStatusMeta,
  TAG_CHIP_STYLE,
} from '../../util/evaluationStatusMeta';
import { formatStockholmDay } from './reviewFormat';
import styles from './page-supplier-evaluations.module.scss';

export type RecipientRow = RequestStatus['recipients'][number];

export type RowActions = {
  busyId: string | null;
  onResend: (recipient: RecipientRow) => void;
  onCopyLink: (recipient: RecipientRow) => void;
  onChangeEmail: (recipient: RecipientRow) => void;
  onRevoke: (recipient: RecipientRow) => void;
};

type Props = {
  recipients: RecipientRow[];
  onShowAnswer: (responseId: string) => void;
  onAnswerNow: (recipient: RecipientRow) => void;
  answeringId: string | null;
  actions: RowActions | null;
};

const RESENDABLE: string[] = [
  EvaluationRecipientStatus.Sent,
  EvaluationRecipientStatus.Opened,
  EvaluationRecipientStatus.Overdue,
];

const RETRYABLE: string[] = [EvaluationRecipientStatus.Failed, EvaluationRecipientStatus.NotSent];

const RESOLVED: string[] = [
  EvaluationRecipientStatus.Answered,
  EvaluationRecipientStatus.Declined,
  EvaluationRecipientStatus.Revoked,
  EvaluationRecipientStatus.Closed,
];

const LATE_STYLE = requestStatusMeta(EvaluationRequestStatus.Overdue);

const TABULAR = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const dayOrDash = (instant: string | null) => (instant ? formatStockholmDay(instant) : '–');

const isViewersRow = (recipient: RecipientRow) =>
  isOwnRecipientOption({ _id: recipient.userId, email: recipient.email });

const RecipientTable: FC<Props> = ({
  recipients,
  onShowAnswer,
  onAnswerNow,
  answeringId,
  actions,
}) => {
  const [t] = useTranslation('evaluations');

  const columns: Column<RecipientRow>[] = useMemo(() => {
    const renderSend = (recipient: RecipientRow) => {
      if (!actions || recipient.isSelf) return null;
      const label = RESENDABLE.includes(recipient.status)
        ? t('status.row.resend', 'Skicka igen')
        : RETRYABLE.includes(recipient.status) && t('status.row.retry', 'Försök igen');
      return (
        label && (
          <BtButton
            size="xxs"
            color="white"
            tabIndex={0}
            loaderShow={actions.busyId === recipient._id}
            onClick={() => actions.onResend(recipient)}
          >
            {label}
          </BtButton>
        )
      );
    };

    const renderPrimary = (recipient: RecipientRow) => {
      if (recipient.canAnswerHere) {
        return (
          <BtButton
            size="xxs"
            color="white"
            tabIndex={0}
            loaderShow={answeringId === recipient._id}
            onClick={() => onAnswerNow(recipient)}
          >
            {t('status.row.answerNow', 'Svara nu')}
          </BtButton>
        );
      }
      const { responseId } = recipient;
      if (responseId) {
        return (
          <BtButton size="xxs" color="white" tabIndex={0} onClick={() => onShowAnswer(responseId)}>
            {t('review.comments.showAnswer', 'Visa svar')}
          </BtButton>
        );
      }
      return renderSend(recipient);
    };

    const renderMenu = (recipient: RecipientRow) => {
      if (!actions) return null;
      if (RESOLVED.includes(recipient.status)) {
        return <span className={styles.menuSlot} aria-hidden="true" />;
      }
      const options = [
        ...(recipient.hasLink
          ? [
              {
                label: t('status.row.copyLink', 'Kopiera länk'),
                action: () => actions.onCopyLink(recipient),
              },
            ]
          : []),
        ...(recipient.isSelf
          ? []
          : [
              {
                label: t('status.row.changeEmail', 'Byt e-post'),
                action: () => actions.onChangeEmail(recipient),
              },
            ]),
        { label: t('status.row.revoke', 'Återkalla'), action: () => actions.onRevoke(recipient) },
      ];
      return (
        <div onClick={event => event.stopPropagation()}>
          <OptionsMenu options={options} />
        </div>
      );
    };

    return [
      {
        title: t('status.recipients.recipient', 'Mottagare'),
        field: 'name',
        render: recipient => (
          <div className={styles.recipient}>
            <div className={styles.nameLine}>
              <span className={styles.author}>{recipient.name || recipient.email}</span>
              {isViewersRow(recipient) && (
                <Badge
                  color={TAG_CHIP_STYLE.color}
                  backgroundColor={TAG_CHIP_STYLE.backgroundColor}
                >
                  {t('status.row.you', 'Du')}
                </Badge>
              )}
            </div>
            {recipient.title && <span className={styles.muted}>{recipient.title}</span>}
            {recipient.email && recipient.name && (
              <span className={`${styles.muted} ${styles.recipientEmail}`}>{recipient.email}</span>
            )}
          </div>
        ),
      },
      {
        title: t('status.recipients.status', 'Status'),
        field: 'status',
        width: 170,
        render: recipient => {
          const meta = recipientStatusMeta(recipient.status);
          const declinedOn =
            recipient.status === EvaluationRecipientStatus.Declined && recipient.declinedAt;
          return (
            <div className={styles.recipient}>
              <Badge color={meta.color} backgroundColor={meta.backgroundColor}>
                {t(meta.labelKey, meta.fallback)}
              </Badge>
              {declinedOn && (
                <span className={`${styles.small} ${styles.muted}`}>
                  {formatStockholmDay(declinedOn)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: t('status.recipients.sent', 'Skickad'),
        field: 'sentAt',
        width: 120,
        cellStyle: TABULAR,
        render: recipient =>
          recipient.isSelf ? t('status.row.noEmail', 'Inget mejl') : dayOrDash(recipient.sentAt),
      },
      {
        title: t('status.recipients.answered', 'Besvarad'),
        field: 'completedAt',
        width: 120,
        cellStyle: TABULAR,
        render: recipient => dayOrDash(recipient.completedAt),
      },
      {
        title: t('status.recipients.score', 'Snitt'),
        field: 'score',
        width: 120,
        render: recipient =>
          recipient.responseId ? (
            <span className={styles.question}>
              <span className={styles.barValue}>{formatScore(recipient.score)}</span>
              {recipient.late && (
                <Badge color={LATE_STYLE.color} backgroundColor={LATE_STYLE.backgroundColor}>
                  {t('response.late', 'Sen')}
                </Badge>
              )}
            </span>
          ) : (
            '–'
          ),
      },
      {
        title: '',
        field: 'responseId',
        width: 140,
        align: 'right',
        sorting: false,
        render: recipient => (
          <div className={styles.rowActions}>
            {renderPrimary(recipient)}
            {renderMenu(recipient)}
          </div>
        ),
      },
    ];
  }, [t, onShowAnswer, onAnswerNow, answeringId, actions]);

  const rows = useMemo(() => recipients.map(recipient => ({ ...recipient })), [recipients]);

  return <DataTable<RecipientRow> isLoading={false} columns={columns} data={rows} />;
};

export default RecipientTable;
